import { useState, useRef, useEffect } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const faqs = [
  {
    q: 'Who is the best attorney near me for my case?',
    a: 'The right attorney depends on experience, strategy, and communication. Gamble & Garcia focuses on results through strong legal positioning, clear guidance, and aggressive representation — helping clients in Santa Maria, San Luis Obispo, and across the Central Coast navigate disputes with confidence and clarity.',
  },
  {
    q: 'How much does it cost to hire an attorney?',
    a: 'Legal fees vary based on case complexity and services required. Most matters begin with a free consultation to evaluate your situation and outline potential costs — ensuring full transparency before any legal work begins. We believe in clear, upfront communication about fees.',
  },
  {
    q: 'How long does a legal case take?',
    a: 'Timelines depend on case type, court schedules, and whether the matter settles or goes to trial. Some cases resolve quickly through negotiation; others require litigation and take longer. During your consultation, we provide a realistic timeline based on your specific situation.',
  },
  {
    q: 'What areas of law does Gamble & Garcia handle?',
    a: 'The firm handles criminal defense (drug crimes, federal charges, expungements), personal injury (auto accidents, wrongful death), family law (divorce, custody, support), and civil litigation. We serve clients throughout Santa Maria, San Luis Obispo, Santa Barbara, and surrounding Central Coast communities.',
  },
  {
    q: 'Do you offer free consultations?',
    a: 'Yes. We offer free initial consultations to evaluate your case, understand your situation, and discuss your legal options. This gives you the opportunity to meet with our attorneys, ask questions, and determine the best path forward — with no obligation and no pressure.',
  },
  {
    q: 'What should I bring to my first consultation?',
    a: 'Bring any documents related to your case — contracts, police reports, court notices, correspondence, or other relevant paperwork. The more information you provide, the better we can evaluate your situation and develop an effective legal strategy tailored to your needs.',
  },
];

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const { ref, inView } = useInView();

  return (
    <section id="faq" className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

          {/* Left sticky label */}
          <div className={`lg:col-span-2 transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
                <span className="text-[#C9A84C] text-[10px] tracking-[0.45em] uppercase font-medium">FAQ</span>
              </div>
              <h2 className="font-serif text-[#0D0D0D] text-4xl md:text-5xl font-bold leading-[1.1] mb-5">
                Frequently<br />Asked<br />Questions
              </h2>
              <p className="text-[#777] text-base leading-relaxed mb-10">
                Common questions about working with Gamble &amp; Garcia, Attorneys at Law — serving the Central Coast since 1974.
              </p>
              <a
                href="tel:8059228308"
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#0D0D0D] text-white text-[11px] font-bold tracking-[0.22em] uppercase hover:bg-[#C9A84C] hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                (805) 922-8308
              </a>
            </div>
          </div>

          {/* Right accordion */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="divide-y divide-[#F0EDE6]">
              {faqs.map((faq, i) => (
                <div key={i}>
                  <button
                    onClick={() => setOpen(open === i ? null : i)}
                    className="w-full flex items-start justify-between py-6 text-left cursor-pointer group gap-4"
                  >
                    <span
                      className={`font-semibold text-[15px] leading-snug transition-colors duration-300 ${
                        open === i ? 'text-[#C9A84C]' : 'text-[#111] group-hover:text-[#C9A84C]'
                      }`}
                    >
                      {faq.q}
                    </span>
                    <div
                      className="w-7 h-7 flex items-center justify-center flex-shrink-0 border transition-colors duration-300 mt-0.5"
                      style={{ borderColor: open === i ? '#C9A84C' : '#ddd', color: open === i ? '#C9A84C' : '#aaa' }}
                    >
                      <i className={`text-sm transition-transform duration-300 ${open === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-400 ease-in-out ${open === i ? 'max-h-80 pb-6' : 'max-h-0'}`}
                  >
                    <p className="text-[#555] text-[15px] leading-[1.9]">{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
